import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Card from './Card';
import LineChart from './LineChart';
import BarChart from './BarChart';
import PieChart from './PieChart';
import ASQStatsCard from './ASQStatsCard';

const Dashboard = ({ data, meta, isLoading, updateDashboard }) => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const refetch = () => { if (updateDashboard) updateDashboard({}); };
    i18n.on('languageChanged', refetch);
    return () => i18n.off('languageChanged', refetch);
  }, [i18n, updateDashboard]);

  const account = isLoading ? [] : [
    data.account.adults.total, data.account.children.total,
    data.account.adults.range, data.account.children.range,
  ];

  const engagement = isLoading ? [] : [
    data.engagement.plays.total, data.engagement.plays.range,
    data.engagement.children.range, data.engagement.children.engaged,
  ];

  const interactivity = isLoading ? [] : [
    data.interactivity.uploads.total, data.interactivity.uploads.range,
  ];

  const asq = isLoading ? [] : [
    data.asq.asq3.rangeCompleted, data.asq.asqSe2.rangeCompleted,
    data.asq.asq3.rangePending, data.asq.asqSe2.rangePending,
  ];

  const followUp = !isLoading && meta.followUpChartEnabled ? [
    data.asqFollowUp.asq3.withFollowUp, data.asqFollowUp.asqSe2.withFollowUp,
    data.asqFollowUp.asq3.withoutFollowUp, data.asqFollowUp.asqSe2.withoutFollowUp,
  ] : [];

  const asq3MonitorData = isLoading ? {} : data.asqStats.asq3.monitor.main;

  const asq3MonitorSections = isLoading ? [] : [
    data.asqStats.asq3.monitor.sections.communication,
    data.asqStats.asq3.monitor.sections.fineMotor, data.asqStats.asq3.monitor.sections.grossMotor,
    data.asqStats.asq3.monitor.sections.personalSocial, data.asqStats.asq3.monitor.sections.problemSolving,
  ];

  const asq3OnScheduleData = isLoading ? {} : data.asqStats.asq3.onSchedule;

  const asq3ReferData = isLoading ? {} : data.asqStats.asq3.refer.main;

  const asq3ReferSections = isLoading ? [] : [
    data.asqStats.asq3.refer.sections.communication,
    data.asqStats.asq3.refer.sections.fineMotor, data.asqStats.asq3.refer.sections.grossMotor,
    data.asqStats.asq3.refer.sections.personalSocial, data.asqStats.asq3.refer.sections.problemSolving,
  ];

  const asq2Data = isLoading ? [] : [
    data.asqStats.asqSe2.monitor, data.asqStats.asqSe2.refer,
    data.asqStats.asqSe2.onSchedule,
  ];

  const accountChart = isLoading ? [] : data.account.chart;
  const engagementChart = isLoading ? [] : data.engagement.chart;
  const interactivityChart = isLoading ? [{ label: t('accountPlayers.columns.cardsLiked'), data: {} }, { label: t('dashboard.cardsLevels'), data: {} }] : data.interactivity.chart;
  const asqChart = isLoading ? [] : data.asq.chart;

  const cls = 'column medium-8 small-12';
  return (
    <div className='row u-margin-top--large'>
      <Card data={account} title={t('dashboard.familyRegistration')} isLoading={isLoading} />
      <LineChart data={accountChart} chartType='account' title={t('dashboard.enrolledChildren')} cls={cls} isLoading={isLoading} />
      <Card data={engagement} title={t('dashboard.engagement')} isLoading={isLoading} />
      <LineChart data={engagementChart} chartType='account' title={t('dashboard.cardsPlayed')} cls={cls} isLoading={isLoading} />
      <Card data={interactivity} title={t('dashboard.interactivity')} isLoading={isLoading} />
      <BarChart data={interactivityChart} chartTypes={['liked', 'levels']} cls={cls} isLoading={isLoading} />
      <Card data={asq} title={t('dashboard.asqStatus')} isLoading={isLoading} />
      <LineChart data={asqChart} chartType='account' title='' cls={cls} isLoading={isLoading} />
      {meta.followUpChartEnabled && (
        <Fragment>
          <Card data={followUp} title={t('dashboard.followUpStatus')} isLoading={isLoading} />
          {data.asqFollowUp.charts.map((chart, i) => <PieChart
            key={i}
            data={chart.questions}
            title={chart.title}
            cls={`column medium-${8 / data.asqFollowUp.charts.length} small-12`}
            emptyText={t('dashboard.followUpFormNote')}
            isLoading={isLoading}
          />)}
        </Fragment>
      )}
      <ASQStatsCard asq3MonitorData={asq3MonitorData} asq3MonitorSections={asq3MonitorSections}
        asq3OnScheduleData={asq3OnScheduleData}
        asq3ReferData={asq3ReferData} asq3ReferSections={asq3ReferSections}
        asq2Data={asq2Data} title={t('dashboard.asqResults')}
        meta={{ directionUp: true }}
        isLoading={isLoading}
      />
    </div>
  );
};

Dashboard.propTypes = {
  data: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  updateDashboardAccount: PropTypes.func,
  updateDashboardASQ: PropTypes.func,
  updateDashboardASQStats: PropTypes.func,
  updateDashboardEngagement: PropTypes.func,
  updateDashboardInteractivity: PropTypes.func,
  updateDashboardFollowUp: PropTypes.func,
  updateDashboard: PropTypes.func,
  isLoading: PropTypes.bool.isRequired,
};

export default Dashboard;
